import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, FilePdf, FileDoc, ArrowLeft, CreditCard, CheckCircle } from '@phosphor-icons/react';
import { doc, getDoc, updateDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {useFirebaseAuth} from "../context/FirebaseAuthContext";
import {db} from "../lib/firebase";
import {formatDate, getReadinessColor, getScoreColor} from "../utils/helpers";
import {Button} from "../components/ui/button";
import {Card} from "../components/ui/card";
import {PaystackButton} from "react-paystack";
import SEO from "../seo/SEO";

const ExportPage = () => {
    const { assessmentId } = useParams();
    const navigate = useNavigate();
    const { user, userData } = useFirebaseAuth();
    const [assessment, setAssessment] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paying, setPaying] = useState(false);
    const [hasPaid, setHasPaid] = useState(false);
    const [pricing, setPricing] = useState({ export_price: 50, currency: 'ZAR' });
    const [checkingPayment, setCheckingPayment] = useState(true);
    const [paymentReference, setPaymentReference] = useState(null);

    const config = {
        reference: (new Date()).getTime().toString(),
        email: user?.email || '',
        amount: pricing.export_price * 100,
        publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_d1b62c4a430407c1a991be4d6c4b258b83b896b6',
        currency: pricing.currency,
    };

    useEffect(() => {
        if (user) {
            fetchInitialData();
        } else {
            navigate('/');
            toast.error('Please login to access this page');
        }
    }, [assessmentId, user]);

    const fetchInitialData = async () => {
        if (!user) return;

        try {
            // Fetch assessment details from Firestore
            const assessmentRef = doc(db, 'assessments', assessmentId);
            const assessmentDoc = await getDoc(assessmentRef);

            if (!assessmentDoc.exists()) {
                toast.error('Assessment not found');
                navigate('/dashboard');
                return;
            }

            const assessmentData = { id: assessmentDoc.id, ...assessmentDoc.data() };

            // Check if user owns this assessment
            if (assessmentData.userId !== user.uid && userData?.role !== 'admin') {
                toast.error('You do not have permission to access this assessment');
                navigate('/dashboard');
                return;
            }

            setAssessment(assessmentData);

            // Check if payment has been made for this assessment
            await checkPaymentStatus(assessmentId);

            // Fetch pricing from Firestore config
            // const configRef = doc(db, 'config', 'pricing');
            // const configDoc = await getDoc(configRef);
            // if (configDoc.exists()) {
            //     setPricing(configDoc.data());
            // }

        } catch (error) {
            console.error('Error fetching assessment:', error);
            toast.error('Failed to load assessment data');
            navigate('/dashboard');
        } finally {
            setCheckingPayment(false);
        }
    };

    const handlePaystackSuccessAction = (reference) => {
        // Implementation for whatever you want to do with reference and after success call.
        handlePayment(reference);
    };

    // you can call this function anything
    const handlePaystackCloseAction = () => {
        // implementation for  whatever you want to do when the Paystack dialog closed.
        console.log('closed')
    }

    const componentProps = {
        ...config,
        text: `Pay R${pricing.export_price} with Paystack`,
        onSuccess: (reference) => handlePaystackSuccessAction(reference),
        onClose: handlePaystackCloseAction,
    };

    const checkPaymentStatus = async (assessmentId) => {
        try {
            const paymentsRef = collection(db, 'payments');
            const paymentsQuery = query(
                paymentsRef,
                where('assessmentId', '==', assessmentId),
                where('userId', '==', user.uid),
                where('status', '==', 'completed')
            );
            const paymentsSnapshot = await getDocs(paymentsQuery);

            if (!paymentsSnapshot.empty) {
                setHasPaid(true);
                const payment = paymentsSnapshot.docs[0];
                setPaymentReference(payment.id);
            }
        } catch (error) {
            console.error('Error checking payment status:', error);
        }
    };

    const handlePayment = async (reference) => {
        setPaying(true);
        try {
            // Create a payment record in Firestore
            const paymentData = {
                userId: user.uid,
                assessmentId: assessmentId,
                amount: pricing.export_price,
                currency: pricing.currency,
                purpose: 'export',
                status: "completed",
                created_at: new Date().toISOString(),
                user_email: user.email,
                user_name: userData?.full_name || user.displayName,
                paymentReference: reference.reference
            };

            await addDoc(collection(db, 'payments'), paymentData);

        } catch (error) {
            console.error('Payment initialization error:', error);
            toast.error('Failed to initialize payment. Please try again.');
            setPaying(false);
        }
    };

    const generatePDF = async () => {
        setLoading(true);
        try {
            // Create a temporary div for PDF generation
            const pdfContent = document.createElement('div');
            pdfContent.innerHTML = `
        <div style="padding: 40px; font-family: Arial, sans-serif;">
          <div style="text-align: center; margin-bottom: 40px;">
            <h1 style="color: #002FA7;">ECSA Assessment Report</h1>
            <p style="color: #666;">Generated on ${formatDate(new Date())}</p>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2>Assessment Summary</h2>
            <div style="display: flex; gap: 20px; margin-top: 20px;">
              <div style="flex: 1; padding: 20px; background: #f0f0f0; border-radius: 8px;">
                <p style="color: #666;">Overall Score</p>
                <p style="font-size: 48px; font-weight: bold; color: ${getScoreColor(assessment.overall_score)}">
                  ${assessment.overall_score}/100
                </p>
              </div>
              <div style="flex: 1; padding: 20px; background: #f0f0f0; border-radius: 8px;">
                <p style="color: #666;">Readiness Level</p>
                <p style="font-size: 24px; font-weight: bold; color: #002FA7;">
                  ${assessment.readiness_level}
                </p>
              </div>
            </div>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2>ECSA Outcome Scores</h2>
            ${assessment.outcome_scores?.map(outcome => `
              <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
                  <strong>ECSA-${String(outcome.outcome_number).padStart(2, '0')}</strong>
                  <span style="color: ${getScoreColor(outcome.score)}; font-weight: bold;">${outcome.score}/100</span>
                </div>
                <div style="height: 10px; background: #f0f0f0; border-radius: 5px; overflow: hidden;">
                  <div style="width: ${outcome.score}%; height: 100%; background: ${getScoreColor(outcome.score)};"></div>
                </div>
                <p style="margin-top: 10px; color: #666; font-size: 14px;">${outcome.evidence || ''}</p>
              </div>
            `).join('')}
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2>Key Strengths</h2>
            <ul>
              ${assessment.strengths?.map(strength => `<li>${strength}</li>`).join('')}
            </ul>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2>Areas for Improvement</h2>
            <ul>
              ${assessment.areas_for_improvement?.map(area => `<li>${area}</li>`).join('')}
            </ul>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2>Recommendations</h2>
            <div style="padding: 20px; background: #f9f9f6; border-radius: 8px;">
              <p>${assessment.recommendations || ''}</p>
            </div>
          </div>
          
          <div style="margin-top: 50px; text-align: center; color: #666; font-size: 12px;">
            <p>MyProfessionalPath Platform - Professional Engineer Readiness Report</p>
            <p>This report is generated by AI and should be reviewed by a professional mentor</p>
          </div>
        </div>
      `;

            document.body.appendChild(pdfContent);

            const canvas = await html2canvas(pdfContent, {
                scale: 2,
                logging: false,
                useCORS: true
            });

            document.body.removeChild(pdfContent);

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(`ECSA_Assessment_${assessmentId}.pdf`);

            // Update download count in Firestore
            const assessmentRef = doc(db, 'assessments', assessmentId);
            await updateDoc(assessmentRef, {
                download_count: (assessment.download_count || 0) + 1,
                last_downloaded_at: new Date().toISOString()
            });

            toast.success('PDF downloaded successfully!');
        } catch (error) {
            console.error('PDF generation error:', error);
            toast.error('Failed to generate PDF');
        } finally {
            setLoading(false);
        }
    };

    const generateWord = async () => {
        setLoading(true);
        try {
            // Create Word document content
            const wordContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>MyProfessionalPath Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            h1 { color: #002FA7; }
            .header { text-align: center; margin-bottom: 40px; }
            .score-card { background: #f0f0f0; padding: 20px; margin: 20px 0; border-radius: 8px; }
            .outcome { margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
            .strength { background: #e6f4ea; padding: 10px; margin: 5px 0; }
            .improvement { background: #fff3e0; padding: 10px; margin: 5px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ECSA Assessment Report</h1>
            <p>Generated on ${formatDate(new Date())}</p>
          </div>
          
          <div class="score-card">
            <h2>Assessment Summary</h2>
            <p><strong>Overall Score:</strong> ${assessment.overall_score}/100</p>
            <p><strong>Readiness Level:</strong> ${assessment.readiness_level}</p>
            <p><strong>Model Used:</strong> ${assessment.model_used || 'AI Model'}</p>
          </div>
          
          <h2>ECSA Outcome Scores</h2>
          ${assessment.outcome_scores?.map(outcome => `
            <div class="outcome">
              <h3>ECSA-${String(outcome.outcome_number).padStart(2, '0')}</h3>
              <p><strong>Score:</strong> ${outcome.score}/100</p>
              <p><strong>Evidence:</strong> ${outcome.evidence || 'Not specified'}</p>
            </div>
          `).join('')}
          
          <h2>Key Strengths</h2>
          ${assessment.strengths?.map(strength => `<div class="strength">✓ ${strength}</div>`).join('')}
          
          <h2>Areas for Improvement</h2>
          ${assessment.areas_for_improvement?.map(area => `<div class="improvement">• ${area}</div>`).join('')}
          
          <h2>Recommendations</h2>
          <div style="padding: 20px; background: #f9f9f6; border-radius: 8px;">
            <p>${assessment.recommendations || ''}</p>
          </div>
          
          <footer style="margin-top: 50px; text-align: center; color: #666; font-size: 12px;">
            <p>MyProfessionalPath Platform - Professional Engineer Readiness Report</p>
          </footer>
        </body>
        </html>
      `;

            const blob = new Blob([wordContent], { type: 'application/msword' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ECSA_Assessment_${assessmentId}.doc`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            // Update download count
            const assessmentRef = doc(db, 'assessments', assessmentId);
            await updateDoc(assessmentRef, {
                download_count: (assessment.download_count || 0) + 1,
                last_downloaded_at: new Date().toISOString()
            });

            toast.success('Word document downloaded successfully!');
        } catch (error) {
            console.error('Word generation error:', error);
            toast.error('Failed to generate Word document');
        } finally {
            setLoading(false);
        }
    };

    if (!assessment || checkingPayment) {
        return (
            <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-[#666666]">Loading assessment data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9F9F6]">
            <SEO title="Export Report | MyProfessionalPath"
                 description="myprofessionalpath is an online professional development system. The system is aligned with
                 the Engineering Council of South Africa for candidate engineers, technologists and technicians.  Progress
                 can be measured and tracked against the competence statements for the chosen registration category.
                 It can also be used for individual company CPD reporting.. The system is mobile and tablet compatible so
                 records can be accessed or added to on-the-go."
                 canonicalURL="https://myprofessionalpath.web.app" />
            <nav className="sticky-nav bg-white border-b border-[#DCDCDA]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn" className="flex items-center gap-2">
                            <ArrowLeft size={18} /> Back to Dashboard
                        </Button>
                    </div>
                </div>
            </nav>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="mb-8">
                    <h1 className="font-heading text-4xl font-semibold text-[#0A0A0A] mb-3">Export Assessment Report</h1>
                    <p className="text-base text-[#666666]">Download your assessment report for ECSA submission.</p>
                </div>

                <Card className="swiss-card p-8 mb-8">
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <span className={`font-heading text-3xl font-semibold ${getScoreColor(assessment.overall_score)}`}>
                            {assessment.overall_score}/100
                        </span>
                        <span className={`text-sm font-medium px-3 py-1 rounded-sm ${getReadinessColor(assessment.readiness_level)}`}>
                            {assessment.readiness_level}
                        </span>
                        <span className="bg-[#666666]/10 text-[#666666] px-2 py-1 rounded-sm text-xs">
                            {assessment.model_used || 'AI Model'}
                        </span>
                    </div>
                    <p className="text-sm text-[#666666]">Assessment ID: {assessment.id}</p>
                    {assessment.created_at && (
                        <p className="text-xs text-[#666666] mt-2">Created: {formatDate(assessment.created_at)}</p>
                    )}
                </Card>

                {!hasPaid ? (
                    <Card className="swiss-card p-8" data-testid="export-payment-card">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-12 h-12 bg-[#FF5A00]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                                <CreditCard size={24} weight="duotone" className="text-[#FF5A00]" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-heading text-xl font-medium text-[#0A0A0A] mb-2">Unlock Report Export</h3>
                                <p className="text-sm text-[#666666]">
                                    One payment grants you unlimited downloads of this report in both PDF and Word formats.
                                </p>
                            </div>
                        </div>

                        <div className="border border-[#DCDCDA] rounded-sm p-6 mb-6 bg-[#F9F9F6]">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#666666] mb-2">REPORT EXPORT</p>
                            <div className="flex items-baseline gap-2 mb-4">
                                <span className="font-heading text-4xl font-semibold text-[#0A0A0A]">R{pricing.export_price}</span>
                                <span className="text-sm text-[#666666]">/ report</span>
                            </div>
                            <ul className="space-y-2 text-sm text-[#666666]">
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} weight="fill" className="text-[#009944] flex-shrink-0 mt-0.5" />
                                    <span>Professional PDF report ready for ECSA submission</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} weight="fill" className="text-[#009944] flex-shrink-0 mt-0.5" />
                                    <span>Editable Word document for customisation</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} weight="fill" className="text-[#009944] flex-shrink-0 mt-0.5" />
                                    <span>Unlimited re-downloads of both formats</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} weight="fill" className="text-[#009944] flex-shrink-0 mt-0.5" />
                                    <span>All 11 ECSA outcomes with detailed scores and evidence</span>
                                </li>
                            </ul>
                        </div>

                        <Button
                            onClick={handlePayment}
                            disabled={paying}
                            data-testid="pay-export-btn"
                            className="w-full bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm py-6 text-base font-medium flex items-center justify-center gap-2"
                        >
                            <CreditCard size={20} weight="duotone" />
                            {paying ? 'Redirecting to Paystack...' : <PaystackButton {...componentProps} />}
                        </Button>
                        <p className="text-xs text-center text-[#666666] mt-3">
                            Secure payment powered by Paystack. You'll be redirected to complete payment.
                        </p>
                    </Card>
                ) : (
                    <>
                        <div className="mb-6 flex items-center gap-3 p-3 bg-[#009944]/10 rounded-sm" data-testid="export-payment-banner">
                            <CheckCircle size={20} weight="fill" className="text-[#009944]" />
                            <p className="text-sm font-medium text-[#0A0A0A]">Payment confirmed. Download your report below in any format.</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card className="swiss-card p-8 text-center hover:shadow-lg transition-shadow">
                                <div className="w-16 h-16 bg-[#D92D20]/10 rounded-sm flex items-center justify-center mx-auto mb-4">
                                    <FilePdf size={32} weight="duotone" className="text-[#D92D20]" />
                                </div>
                                <h3 className="font-heading text-xl font-medium text-[#0A0A0A] mb-2">PDF Report</h3>
                                <p className="text-sm text-[#666666] mb-6">Professional PDF format ready for printing and submission to ECSA.</p>
                                <Button
                                    onClick={generatePDF}
                                    disabled={loading}
                                    data-testid="export-pdf-btn"
                                    className="w-full bg-[#D92D20] hover:bg-[#B91C1C] text-white rounded-sm flex items-center justify-center gap-2"
                                >
                                    <Download size={18} />
                                    {loading ? 'Generating...' : 'Download PDF'}
                                </Button>
                            </Card>

                            <Card className="swiss-card p-8 text-center hover:shadow-lg transition-shadow">
                                <div className="w-16 h-16 bg-[#002FA7]/10 rounded-sm flex items-center justify-center mx-auto mb-4">
                                    <FileDoc size={32} weight="duotone" className="text-[#002FA7]" />
                                </div>
                                <h3 className="font-heading text-xl font-medium text-[#0A0A0A] mb-2">Word Document</h3>
                                <p className="text-sm text-[#666666] mb-6">Editable Word format for customization before submission.</p>
                                <Button
                                    onClick={generateWord}
                                    disabled={loading}
                                    data-testid="export-word-btn"
                                    className="w-full bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm flex items-center justify-center gap-2"
                                >
                                    <Download size={18} />
                                    {loading ? 'Generating...' : 'Download Word'}
                                </Button>
                            </Card>
                        </div>
                    </>
                )}

                <Card className="swiss-card p-6 mt-8 bg-[#F3F3F0]">
                    <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-3">About These Reports</h3>
                    <ul className="text-sm text-[#666666] space-y-2">
                        <li className="flex items-start gap-2">
                            <span className="text-[#009944] font-bold">•</span>
                            <span>Reports include all 11 ECSA outcome scores with detailed evidence</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-[#009944] font-bold">•</span>
                            <span>Key strengths and areas for improvement are highlighted</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-[#009944] font-bold">•</span>
                            <span>Recommendations provided for achieving professional engineer status</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-[#009944] font-bold">•</span>
                            <span>Professional formatting suitable for ECSA application submission</span>
                        </li>
                    </ul>
                </Card>
            </main>
        </div>
    );
};

export default ExportPage;